import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, Sunrise } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Card, NativeSelect } from "@/components/Common";
import { api, errMsg, tzOffset } from "@/lib/api";
import { cn } from "@/lib/utils";

const DEFAULTS = { enabled: false, hour: 8, query: "", remote: false, count: 5, min_score: 60 };
const STEPS = ["What jobs", "When", "How many"];
const HOURS = Array.from({ length: 24 }, (_, h) => ({ value: String(h), label: new Date(2000, 0, 1, h).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) }));
const COUNTS = [3, 5, 10].map((n) => ({ value: String(n), label: `${n} jobs` }));
const hourLabel = (h) => HOURS[Number(h)]?.label;

export const scheduleSummary = (s) =>
  `Every day at ${hourLabel(s.hour)}, find ${s.count} ${s.remote ? "remote " : ""}"${s.query || "…"}" jobs scoring ${s.min_score}%+`;

const Stepper = ({ step, setStep, canSkip }) => (
  <div className="mb-5 flex items-center gap-2" data-testid="schedule-stepper">
    {STEPS.map((l, i) => (
      <button key={l} type="button" onClick={() => (i === 0 || canSkip) && setStep(i)} disabled={i > 0 && !canSkip} data-testid={`schedule-step-${i + 1}`}
        className={cn("flex flex-1 items-center gap-1.5 rounded-full border px-2 py-1 text-[11px] transition-colors", i === step ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F3C649]" : i < step ? "border-[#3F3F46] text-zinc-300" : "border-[#27272A] text-zinc-500")}>
        <span className={cn("flex h-4 w-4 shrink-0 items-center justify-center rounded-full font-mono text-[9px]", i < step ? "bg-[#D4AF37] text-black" : "bg-[#27272A]")}>{i < step ? <Check className="h-2.5 w-2.5" /> : i + 1}</span>
        <span className="truncate">{l}</span>
      </button>
    ))}
  </div>
);

const StepBody = ({ step, s, set }) => {
  if (step === 0) return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-500">Which role should the agent look for each morning?</p>
      <Input value={s.query} onChange={(e) => set({ query: e.target.value })} placeholder="e.g. Product Manager" className="bg-[#09090B]" data-testid="schedule-query-input" />
      <label className="flex items-center gap-2 text-sm text-zinc-300"><Switch checked={!!s.remote} onCheckedChange={(v) => set({ remote: v })} data-testid="schedule-remote-switch" />Remote only</label>
    </div>
  );
  if (step === 1) return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-500">Pick the time your fresh batch should be ready (your local time).</p>
      <NativeSelect value={String(s.hour)} onChange={(v) => set({ hour: v })} options={HOURS} className="w-full" data-testid="schedule-hour-select" />
    </div>
  );
  return (
    <div className="space-y-4">
      <NativeSelect value={String(s.count)} onChange={(v) => set({ count: v })} options={COUNTS} className="w-full" data-testid="schedule-count-select" />
      <div className="space-y-2">
        <div className="flex justify-between text-xs text-zinc-400"><span>Minimum match score</span><span className="font-mono text-[#F3C649]" data-testid="schedule-min-score">{s.min_score}%</span></div>
        <Slider value={[Number(s.min_score)]} min={0} max={100} step={5} onValueChange={([v]) => set({ min_score: v })} data-testid="schedule-min-score-slider" />
      </div>
    </div>
  );
};

export const AutoSchedule = () => {
  const [s, setS] = useState(null);
  const [step, setStep] = useState(0);
  useEffect(() => { api.get("/auto-apply/schedule").then((r) => setS({ ...DEFAULTS, ...r.data })).catch((e) => setS(e.response?.status === 404 ? { ...DEFAULTS } : null)); }, []);
  if (!s) return null;
  const set = (p) => setS((x) => ({ ...x, ...p }));
  const save = async () => {
    try { await api.put("/auto-apply/schedule", { ...s, count: Number(s.count), hour: Number(s.hour), min_score: Number(s.min_score), tz_offset_minutes: tzOffset() }); toast.success(s.enabled ? "Morning autopilot saved" : "Schedule saved (autopilot is off)"); } catch (e) { toast.error(errMsg(e)); }
  };
  const last = step === STEPS.length - 1;
  return (
    <Card data-testid="schedule-card">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-display text-lg text-zinc-100"><Sunrise className="h-4 w-4 text-[#D4AF37]" />Morning autopilot</h3>
        <Switch checked={!!s.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid="schedule-enabled-switch" />
      </div>
      <Stepper step={step} setStep={setStep} canSkip={!!s.query} />
      <StepBody step={step} s={s} set={set} />
      <p className={cn("mt-5 rounded-lg border p-3 text-sm", s.enabled ? "border-[#D4AF37]/30 bg-[#D4AF37]/5 text-zinc-200" : "border-[#27272A] text-zinc-500")} data-testid="schedule-summary">
        {s.enabled ? scheduleSummary(s) : `Off — turn on to: ${scheduleSummary(s).replace(/^E/, "e")}`}
      </p>
      <div className="mt-4 flex gap-2">
        {step > 0 && <Button variant="ghost" onClick={() => setStep(step - 1)} data-testid="schedule-back-button"><ChevronLeft className="h-4 w-4" />Back</Button>}
        {last ? <Button onClick={save} disabled={!s.query} className="flex-1" data-testid="schedule-save-button">Save schedule</Button>
          : <Button variant="outline" onClick={() => setStep(step + 1)} disabled={step === 0 && !s.query} className="flex-1" data-testid="schedule-next-button">Next<ChevronRight className="h-4 w-4" /></Button>}
      </div>
    </Card>
  );
};
